import React, { useContext, useRef, useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import useSWR from "swr";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import { t } from "i18next";
import _ from "lodash";
import useQueryParams from "Utils/useQueryParams";
import usePaginatedList from "Utils/usePaginatedList";
import { useLocation } from "react-router-dom";

import { globeColor, users, orderList } from "Icons/index";
import cx from "classnames";
import useFilter from "Utils/useFilter";
import ButtonRefresh from "Components/buttonRefresh/ButtonRefresh";
import { MediaQueriesContext } from "@/App";
import ButtonBack from "Components/buttonBack/ButtonBack";
import XDate from "xdate";
import DayPicker from "Components/dayPicker/DayPicker";
import Select from "Components/select/Select";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";

import { Icon, selectLogEtlive } from "Icons/index";

import qs from "query-string";

const FilterCard = ({ onFilterClick, width, className, label, icon, cardBorderColor, isFilterActive = false }) => (
  <div
    style={width && { width }}
    className={cx(
      `px-3 pt-2 pb-3 cursor-pointer flex items-center rounded  border-solid border-[1px] border-[#EBEBEB] relative overflow-hidden gap-3 border-b-0`,
      className,
      { ["bg-[--accent-l35]"]: isFilterActive }
    )}
    onClick={onFilterClick}
  >
    <div
      className={cx("w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center", {
        ["bg-[--bg-d3]"]: !isFilterActive,
      })}
    >
      <Icon svg={icon} width="1.5rem" className="opacity-60" />
    </div>

    <div className="w-full">
      <div className="flex items-center justify-between w-full">
        <div className="text-sm">{label}</div>
      </div>
    </div>

    <div
      className={cx("absolute bottom-0 left-0 right-0  border-0 border-b-4 border-solid ", cardBorderColor, {
        "opacity-10": !isFilterActive,
      })}
    ></div>
  </div>
);

const tableTypeValues = {
  ACCOUNT: "account",
  SINGLE_DOMAIN: "single-domain",
  DOMAINS_RANK: "domains-rank",
};

const DeliveryAnalyticsRow = ({ data, tableType }) => (
  <tr>
    {tableType !== tableTypeValues.DOMAINS_RANK && <td>{data.day}</td>}
    {(tableType === tableTypeValues.SINGLE_DOMAIN || tableType === tableTypeValues.DOMAINS_RANK) && (
      <td>{data.domain}</td>
    )}
    <td>{data.delivered_count}</td>
    <td>{data.bounce_count}</td>
    <td>{data.suppressed_count}</td>
    <td>{data.processed_count}</td>
  </tr>
);

const TableTypeSelect = ({ query }) => {
  const { typeToAccount, typeToSingleDomain, typeToDomainsRank } = useFilter({
    paramName: "type",
    values: [tableTypeValues.ACCOUNT, tableTypeValues.SINGLE_DOMAIN, tableTypeValues.DOMAINS_RANK],
  });

  const { isMediaMobile } = useContext(MediaQueriesContext);

  return (
    <div className="flex w-full gap-3">
      {[
        {
          label: t("selectionCard.accounts"),
          icon: users,
          isFilterActive: !query.type || query.type === tableTypeValues.ACCOUNT,
          cardBorderColor: "border-qb-blue",
          onFilterClick: typeToAccount,
        },
        {
          label: t("selectionCard.singleDomain"),
          icon: globeColor,
          isFilterActive: query.type === tableTypeValues.SINGLE_DOMAIN,
          cardBorderColor: "border-qb-blue",
          onFilterClick: typeToSingleDomain,
        },
        {
          label: t("selectionCard.domainsRanking"),
          icon: orderList,
          isFilterActive: query.type === tableTypeValues.DOMAINS_RANK,
          cardBorderColor: "border-qb-blue",
          onFilterClick: typeToDomainsRank,
        },
      ].map((filter, i) => (
        <FilterCard
          key={i}
          width="200px"
          label={filter.label}
          icon={filter.icon}
          isFilterActive={filter.isFilterActive}
          cardBorderColor={filter.cardBorderColor}
          onFilterClick={filter.onFilterClick}
        />
      ))}
    </div>
  );
};

const DateRangeControls = () => {
  const location = useLocation();
  const query = qs.parse(location.search);
  const history = useHistory();

  // tracemail has a 60 days limit history
  const [minDate] = useState(new XDate().addDays(-60).toDate());
  const [maxDate] = useState(new XDate().toDate());

  return (
    <div className={cx("flex gap-3")}>
      <DayPicker
        placeholder="Select a day"
        label={t("input.label.date.from")}
        name="from"
        value={query.from ? new XDate(query.from).toDate() : null}
        maxDate={maxDate}
        minDate={minDate}
        onChange={(d) => {
          history.push({
            search: qs.stringify({
              ...query,
              from: new XDate(d.from).toString("yyyy-MM-dd"),
            }),
          });
        }}
      />

      <DayPicker
        name="to"
        label={t("input.label.date.to")}
        value={query.to ? new XDate(query.to).toDate() : null}
        maxDate={maxDate}
        minDate={query.from ? new XDate(query.from).addDays(1).toDate() : minDate}
        onChange={(d) => {
          history.push({
            search: qs.stringify({
              ...query,
              to: new XDate(d.to).toString("yyyy-MM-dd"),
            }),
          });
        }}
      />
    </div>
  );
};

const Controls = ({ className, paginationInfo, accountData, onRefresh, isRefreshing }) => {
  const location = useLocation();
  const query = qs.parse(location.search);
  const description = accountData ? accountData.resources[0].description : "";
  const history = useHistory();

  const { customerCode } = useParams();

  const { data: domains, error: domains_error } = useSWR(`/delivery/tracemail/domains?customer_code=${customerCode}`);

  return (
    <div className={cx("flex flex-col", className)}>
      <TableTypeSelect query={query} />
      {(!query.type || query.type === tableTypeValues.ACCOUNT) && (
        <div className="flex flex-col justify-between w-full pb-2 mt-5 md:flex-row md:items-end">
          <div className="flex flex-col gap-8 md:flex-row md:items-end">
            <div className="text-lg font-semibold">Utenza: {description}</div>

            <DateRangeControls />
          </div>
          <div className="flex items-center justify-between gap-3 mt-4">
            <div className="flex gap-4">
              <ButtonRefresh isLoading={isRefreshing} onClick={onRefresh} />
            </div>
          </div>
        </div>
      )}
      {query.type === tableTypeValues.SINGLE_DOMAIN && (
        <div className="flex flex-col justify-between w-full pb-2 mt-5 md:flex-row md:items-end">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            {/* <div className="text-lg font-semibold">Utenza: {description}</div> */}

            <div className="min-w-[200px]">
              <Select
                isClearable
                label={t("input.label.domain")}
                components={{ DropdownIndicator }}
                name="domain"
                disabled={!domains}
                options={domains?.resources.map((domain) => ({
                  value: domain.name,
                  label: domain.name,
                }))}
                value={query.domain}
                onChange={(e) => {
                  history.push({
                    search: qs.stringify({
                      ...query,
                      domain: e.domain,
                    }),
                  });
                }}
                isActive={!!query.domain}
              />
            </div>

            <DateRangeControls />
          </div>
          <div className="flex items-center justify-between gap-3 mt-4">
            <div className="flex gap-4">
              <ButtonRefresh isLoading={isRefreshing} onClick={onRefresh} />
            </div>
          </div>
        </div>
      )}
      {query.type === tableTypeValues.DOMAINS_RANK && (
        <div className="flex flex-col justify-between w-full pb-2 mt-5 md:flex-row md:items-end">
          <div className="flex flex-col gap-8 md:flex-row md:items-end">
            <div className="text-lg font-semibold">Utenza: {description}</div>
            <DateRangeControls />
          </div>

          <div className="flex items-center justify-between gap-3 mt-4">
            <div className="flex gap-4">
              <ButtonRefresh isLoading={isRefreshing} onClick={onRefresh} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Analytics = () => {
  const history = useHistory();
  const queryParams = useQueryParams();
  const { accountCode } = useParams();

  const {
    data: tableData,
    error,
    isRefreshing,
    refreshList,
    isFiltering,
  } = usePaginatedList(`/delivery/tracemail/daily/${accountCode}?${queryParams}`);
  const { data: accountData, mutate } = useSWR(`/delivery/accounts/${accountCode}`);

  const { resources, pagination } = tableData || {};

  const tableType = queryParams.get("type");

  const commonHeaders = [
    {
      name: t("table.header.delivered"),
      sortable: true,
      key: "delivered_count",

      activeSorting: queryParams.get("sort_field") === "delivered_count" && queryParams.get("sort"),
    },
    {
      name: t("table.header.not.delivered"),
      sortable: true,
      key: "bounce_count",
      activeSorting: queryParams.get("sort_field") === "bounce_count" && queryParams.get("sort"),
    },
    {
      name: t("table.header.suppressed"),
      sortable: true,
      key: "suppressed_count",
      activeSorting: queryParams.get("sort_field") === "suppressed_count" && queryParams.get("sort"),
    },
    {
      name: t("table.header.processed"),
      sortable: true,
      key: "processed_count",
      activeSorting: queryParams.get("sort_field") === "processed_count" && queryParams.get("sort"),
    },
  ];

  const getTableHeaders = () => {
    if (!tableType || tableType === tableTypeValues.ACCOUNT) {
      return _.compact([
        {
          name: t("table.header.date"),
          sortable: true,
          key: "day",
          activeSorting: queryParams.get("sort_field") === "day" && queryParams.get("sort"),
        },
        ...commonHeaders,
      ]);
    } else if (tableType === tableTypeValues.SINGLE_DOMAIN) {
      return [
        {
          name: t("table.header.date"),
          center: true,
        },
        {
          name: t("table.header.domain"),
        },
        ...commonHeaders,
      ];
    } else if (tableType === tableTypeValues.DOMAINS_RANK) {
      return [
        {
          name: t("table.header.domain"),
        },
        ...commonHeaders,
      ];
    }
    return [...commonHeaders];
  };

  const refresh = () => {
    refreshList();
    mutate();
  };

  const goBack = () => history.push(location.pathname.split("/").slice(0, -1).join("/"));

  return (
    <div className="flex flex-col h-full px-4 pt-4 max-w-[100vw]">
      <div className="h-8 max-w-fit">
        <ButtonBack onClick={goBack} />
      </div>
      <div className="text-xl font-semibold">{t("dashboard.deliveryAnalytics.title")}</div>

      <Controls
        className="mt-6"
        paginationInfo={pagination}
        accountData={accountData}
        onRefresh={refresh}
        isRefreshing={isRefreshing}
      />
      <div className="flex flex-col flex-1 mt-4 overflow-x-scroll overflow-y-scroll">
        {tableType === tableTypeValues.SINGLE_DOMAIN && !queryParams.get("domain") ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Icon svg={selectLogEtlive} width="10rem" height="10rem" />
            <div className="text-xl font-medium">{t("infoMsg.selectDomainFirst")}</div>
          </div>
        ) : (
          <ResponsiveTable
            className="min-w-[600px]"
            history={history}
            location={location}
            areRowsLoading={!resources || isRefreshing}
            addMsg={t("infoMsg.noRecords.msg")}
            addDesc={t("infoMsg.noRecords.desc")}
            headers={getTableHeaders()}
            rows={
              resources
                ? resources.map((resource, i) => <DeliveryAnalyticsRow key={i} data={resource} tableType={tableType} />)
                : []
            }
          />
        )}
      </div>
    </div>
  );
};

export default Analytics;
